import { useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover'

interface MultiSelectFilterProps {
  label: string
  placeholder?: string
  options: string[]
  selected: string[]
  onChange: (values: string[]) => void
}

export function MultiSelectFilter({
  label,
  placeholder,
  options,
  selected,
  onChange,
}: MultiSelectFilterProps) {
  const [search, setSearch] = useState('')

  const filtered = options.filter((opt) =>
    opt.toLowerCase().includes(search.toLowerCase())
  )

  function buttonLabel(): string {
    if (selected.length === 0) return `All ${label}`
    if (selected.length === 1) return selected[0]
    return `${selected.length} ${label}`
  }

  function toggle(value: string) {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value))
    } else {
      onChange([...selected, value])
    }
  }

  const isActive = selected.length > 0

  return (
    <Popover onOpenChange={(open) => { if (!open) setSearch('') }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors focus:outline-none',
            isActive
              ? 'border-primary/40 bg-primary/5 font-medium text-primary hover:bg-primary/10'
              : 'border-input bg-white text-gray-700 hover:bg-gray-50'
          )}
        >
          <span className="max-w-[160px] truncate">{buttonLabel()}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={placeholder ?? `Search ${label}…`}
          className="mb-1.5 w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-sm placeholder:text-gray-400 focus:border-primary focus:outline-none"
          autoFocus
        />
        <div className="max-h-52 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="px-2 py-3 text-center text-xs text-gray-400">No results</p>
          ) : (
            filtered.map((opt) => {
              const checked = selected.includes(opt)
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => toggle(opt)}
                  className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50"
                >
                  <span
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                      checked
                        ? 'border-primary bg-primary text-white'
                        : 'border-gray-300 bg-white'
                    )}
                  >
                    {checked && <Check className="h-3 w-3" />}
                  </span>
                  <span className="truncate">{opt}</span>
                </button>
              )
            })
          )}
        </div>
        {selected.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="mt-1.5 w-full rounded-md px-2 py-1.5 text-center text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-700"
          >
            Clear selection
          </button>
        )}
      </PopoverContent>
    </Popover>
  )
}
